<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Animierter Background -->
    <div class="fixed inset-0 -z-10">
      <!-- Gradient Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 dark:from-gray-900 dark:via-blue-950/20 dark:to-gray-800"></div>
      
      <!-- Animierte Gradient Blobs -->
      <div class="absolute top-0 -left-4 w-96 h-96 bg-blue-400/20 dark:bg-blue-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob"></div>
      <div class="absolute top-0 -right-4 w-96 h-96 bg-purple-400/20 dark:bg-purple-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      <div class="absolute -bottom-8 left-20 w-96 h-96 bg-pink-400/20 dark:bg-pink-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
      
      <!-- Grid Pattern Overlay -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)]"></div>
    </div>
    
    <Navigation />
    
    <!-- Hero Section -->
    <section class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-32 md:pt-40">
      <div 
        ref="heroElement"
        class="text-center mb-12 transition-all duration-700 ease-out"
        :class="heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
      >
        <h1 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Lassen Sie uns zusammenarbeiten
        </h1>
        <div class="mt-4 h-0.5 w-16 bg-blue-600 dark:bg-blue-400 mx-auto"></div>
        <p class="mt-6 text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Lassen Sie uns gemeinsam Ihre digitale Vision verwirklichen. 
          Kostenloses Beratungsgespräch – unverbindlich und individuell auf Sie zugeschnitten.
        </p>
      </div>
    </section>

    <!-- Main Content -->
    <section class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>

      <!-- Two Column Layout -->
      <div v-else class="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
        <!-- Left: Contact Form -->
        <div 
          ref="formElement"
          class="transition-all duration-700 ease-out"
          :class="formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
        >
          <div class="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Kontaktformular</h2>
            <ContactForm @submit="handleFormSubmit" />
          </div>
        </div>

        <!-- Right: Contact Info -->
        <div 
          ref="infoElement"
          class="transition-all duration-700 ease-out"
          :class="infoVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
        >
          <ContactInfo :contact-info="contactInfo" />
        </div>
      </div>

      <!-- Trust Badge -->
      <div 
        ref="trustElement"
        class="text-center mt-12 transition-all duration-700 ease-out"
        :class="trustVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
      >
        <p class="text-sm text-gray-500 dark:text-gray-400">
          <span class="inline-flex items-center">
            <svg class="w-4 h-4 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Sichere Kommunikation & Datenschutz garantiert
          </span>
        </p>
      </div>
    </section>

    <!-- Success Animation -->
    <SuccessAnimation
      :show="showSuccessAnimation"
      :summary="formSummary"
      @close="showSuccessAnimation = false"
    />

    <StandardFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Navigation from '../components/Navigation.vue'
import StandardFooter from '../components/StandardFooter.vue'
import ContactForm from '../components/contact/ContactForm.vue'
import ContactInfo from '../components/contact/ContactInfo.vue'
import SuccessAnimation from '../components/contact/SuccessAnimation.vue'
import { getContactInfo, getDefaultContactInfo, type ContactInfo as ContactInfoType } from '../firebase/contact'
import { useScrollAnimation } from '../composables/useScrollAnimation'
import { formatContactData } from '../utils/contactHelpers'
import { logger } from '../utils/logger'
import { useErrorHandler } from '../composables/useErrorHandler'

const contactInfo = ref<ContactInfoType | null>(null)
const loading = ref(true)
const { handleError } = useErrorHandler()
const showSuccessAnimation = ref(false)
const formSummary = ref<{
  name: string
  email: string
  interestLabel?: string
  projectTypeLabel?: string
  budgetLabel?: string
} | undefined>(undefined)

// Scroll-Animationen
const { isVisible: heroVisible, targetElement: heroElement } = useScrollAnimation({
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
})

const { isVisible: formVisible, targetElement: formElement } = useScrollAnimation({
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
})

const { isVisible: infoVisible, targetElement: infoElement } = useScrollAnimation({
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
})

const { isVisible: trustVisible, targetElement: trustElement } = useScrollAnimation({
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
})

const handleFormSubmit = async (data: {
  name: string
  email: string
  interest?: string
  projectType?: string
  budget?: string
  message: string
  privacyAccepted: boolean
}) => {
  try {
    // Speichere in Firestore
    const { submitContactForm } = await import('../firebase/contact')
    await submitContactForm(data)
    
    // Formatiere Daten für Zusammenfassung
    const formattedData = formatContactData(data)
    formSummary.value = {
      name: formattedData.name,
      email: formattedData.email,
      interestLabel: formattedData.interestLabel || undefined,
      projectTypeLabel: formattedData.projectTypeLabel || undefined,
      budgetLabel: formattedData.budgetLabel || undefined
    }
    
    // Zeige Erfolgsanimation
    showSuccessAnimation.value = true
    
    // E-Mail-Versand erfolgt automatisch über Firestore Trigger (sendContactEmail)
    // Der Trigger wird ausgelöst, wenn ein Dokument in contactSubmissions erstellt wird
    // Keine zusätzliche HTTP-Anfrage oder EmailJS-Versand nötig
  } catch (error) {
    handleError(error, 'Fehler beim Absenden des Formulars')
    throw error
  }
}

onMounted(async () => {
  try {
    const data = await getContactInfo()
    if (data) {
      contactInfo.value = data
    } else {
      // Fallback zu Standard-Werten
      const defaultData = getDefaultContactInfo()
      contactInfo.value = {
        ...defaultData,
        updatedAt: new Date()
      } as ContactInfoType
    }
  } catch (error) {
    logger.error('Fehler beim Laden der Kontaktdaten', error)
    // Fallback zu Standard-Werten
    const defaultData = getDefaultContactInfo()
    contactInfo.value = {
      ...defaultData,
      updatedAt: new Date()
    } as ContactInfoType
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}
.animate-blob {
  animation: blob 7s infinite;
}
.animation-delay-2000 {
  animation-delay: 2s;
}
.animation-delay-4000 {
  animation-delay: 4s;
}
</style>

